import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  PerformanceTargetStrategySupportedStrategiesEnum,
  PerformanceTargetStrategySupportedStrategiesEnumValues,
} from 'generated/resources/datatable.graphql'
import {
  PerformanceProcessLocationsFilterOptionsDocument,
  PerformanceProcessTeamsFilterOptionsDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import MultipleChoice from 'modules/core/components/Filter/filters/MultipleChoice'
import SingleChoice from 'modules/core/components/Filter/filters/SingleChoice'
import Loading from 'modules/core/components/Loading'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import { isEnumValue } from 'modules/core/lib/utils/enums'

import { dynamicOptions } from './dynamicOptions'

interface Props {
  primaryFilter: PerformanceTargetStrategySupportedStrategiesEnum | undefined | null
  setPrimaryFilter: (value: PerformanceTargetStrategySupportedStrategiesEnum | undefined) => void
  secondaryFilter: number[]
  setSecondaryFilter: (value: number[]) => void
  onSubmit: () => void
}

const DynamicFilter = ({
  primaryFilter,
  setPrimaryFilter,
  secondaryFilter,
  setSecondaryFilter,
  onSubmit,
}: Props) => {
  const { data: locationsData, loading: locationsLoading } = useQuery(
    PerformanceProcessLocationsFilterOptionsDocument
  )
  const { data: teamsData, loading: teamsLoading } = useQuery(
    PerformanceProcessTeamsFilterOptionsDocument
  )

  const locationsOptions = locationsData?.locations.locations
  const teamsOptions = teamsData?.teams.teams

  const filteredSecondaryOptions = primaryFilter
    ? primaryFilter === 'by_locations'
      ? locationsOptions ?? []
      : teamsOptions ?? []
    : []

  const onChangePrimaryFilter = (
    selectedPrimaryFilter: PerformanceTargetStrategySupportedStrategiesEnum
  ) => {
    setPrimaryFilter(selectedPrimaryFilter)
    setSecondaryFilter([])
  }

  if (locationsLoading || teamsLoading) return <Loading />

  return (
    <>
      <Text size="100" weight="semibold">
        {i18n.t('performance_process.reviews.condition')}
      </Text>
      <Box
        flexDirection="row"
        justifyContent="spaceBetween"
        alignItems="center"
        paddingTop="s6"
        gap="s6"
        width="fit-content"
      >
        <SingleChoice
          label={i18n.t('performance_process.reviews.filter_by')}
          value={primaryFilter}
          noFooter
          onValueChange={(value) =>
            value &&
            isEnumValue(PerformanceTargetStrategySupportedStrategiesEnumValues, value) &&
            onChangePrimaryFilter(value)
          }
          options={dynamicOptions().map(({ label, id }) => ({
            label: label,
            value: id,
          }))}
        />
        {primaryFilter && locationsOptions && teamsOptions && (
          <>
            <Text size="100" weight="semibold">
              {i18n.t('performance_process.reviews.connection_selector')}
            </Text>

            <MultipleChoice
              label={i18n.t('performance_process.reviews.select')}
              value={secondaryFilter}
              onValueChange={(value) => setSecondaryFilter(value ?? [])}
              options={filteredSecondaryOptions.map(({ name, id }) => ({
                label: name,
                value: id,
              }))}
              hideFooter={!secondaryFilter?.length}
              onClose={onSubmit}
              footer={
                <Box
                  alignItems="center"
                  padding="s12"
                  cursor="pointer"
                  onClick={() => setSecondaryFilter([])}
                >
                  <Text.Normal size="300" weight="medium" color="radicalRed1200">
                    {i18n.t('performance_process.reviews.clear_selection')}
                  </Text.Normal>
                </Box>
              }
            />
          </>
        )}
      </Box>
    </>
  )
}

export default observer(DynamicFilter)
